//react
import React, { useState, useMemo } from "react";
import Highlighter from "react-highlight-words";

//UI
import { Table, Empty } from "antd";
import {
  CardElement,
  HomePageTable,
} from "../../../components/Common/UIElements";
import CustomerPropertiesTopBar from "../../../components/Onboarding/PropertyView/CustomerPropertiesTopBar";
import LoadingComponent from "../../../components/Common/LoadingComponent";

import { ONBOARDING_PROPERTY_STATUS } from "../../../utils/onboarding-helper";
import {
  ColoredTextTag,
  TEXT_TAG_COLORS,
} from "../../../components/Common/ColoredTextTag/ColoredTextTag";
//processing
import { tableStringSorter } from "../../../utils/table";
import { useGetCustomer } from "../../../hooks/Onboarding/onboarding.hooks";
import {
  useGetAllProperties,
  useGetPropertiesByCustomer,
} from "../../../hooks/Onboarding/onboarding-properties.hooks";
import { useGetPMS } from "../../../hooks/pms.hooks";
import useTableSearch from "../../../hooks/table-search.hooks";
import useQuery from "../../../hooks/url.hooks";
import { simpleStringDecode } from "../../../utils/common-utils";
// css
import "./styles.css";

// set table height according to window
let tableHeight = 400;
if (window.innerWidth > 700) {
  tableHeight = window.innerHeight - 375;
}

const OnboardingProperties = () => {
  const query = useQuery();
  const customerId = simpleStringDecode(query.get("customer"));
  const [customer, setCustomer] = useState(customerId ?? "");
  const { data: pmsData } = useGetPMS();

  const { data: apiAllPropertiesData } = useGetAllProperties({
    enabled: customer === "",
  });
  const { data: apiCustomerData } = useGetCustomer(customer, {
    enabled: !!customer,
  });

  const { data: apiPropertiesData, isLoading: isLoadingProperties } =
    useGetPropertiesByCustomer(customer, {
      enabled: !!customer,
    });
  const textColor = (status) => {
    switch (status) {
      case ONBOARDING_PROPERTY_STATUS.APPROVED:
      case ONBOARDING_PROPERTY_STATUS.PROPERTY_CREATED:
        return <ColoredTextTag text={status} color={TEXT_TAG_COLORS.SUCCESS} />;
      case ONBOARDING_PROPERTY_STATUS.PENDING_APPROVAL:
      case ONBOARDING_PROPERTY_STATUS.PENDING_ACTIVATION:
        return <ColoredTextTag text={status} color={TEXT_TAG_COLORS.WARN} />;
      default:
        return <ColoredTextTag text={status} color="black" />;
    }
  };

  // render component for hilightable search text
  const searchHilighter = (dataIndex, customRender) => ({
    render: (text) => {
      return searchedColumns.includes(dataIndex) ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={searchedWords}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : customRender ? (
        customRender(text)
      ) : (
        text
      );
    },
  });

  // table columns
  const columns = [
    {
      title: "Property Name",
      dataIndex: "property_name",
      key: "property_name",
      width: "200px",
      sorter: tableStringSorter("property_name"),
      fixed: "left",
      ...searchHilighter("property_name"),
    },
    {
      title: "Property Code",
      dataIndex: "property_code",
      key: "property_code",
      width: "200px",
      sorter: tableStringSorter("property_code"),
      ...searchHilighter("property_code"),
    },
    {
      title: "Property Brand",
      dataIndex: "brand",
      key: "brand",
      width: "200px",
      sorter: tableStringSorter("brand"),
      ...searchHilighter("brand"),
    },
    {
      title: "PMS",
      dataIndex: "pms",
      key: "pms",
      width: "200px",
      sorter: tableStringSorter("pms"),
      ...searchHilighter("pms"),
    },
    {
      title: "POS",
      dataIndex: "pos",
      key: "pos",
      width: "200px",
      sorter: tableStringSorter("pos"),
      ...searchHilighter("pos"),
    },
    {
      title: "Time Zone",
      dataIndex: "property_time_zone",
      key: "property_time_zone",
      width: "200px",
      sorter: tableStringSorter("property_time_zone"),
      ...searchHilighter("property_time_zone"),
    },
    {
      title: "Property Contact First Name",
      dataIndex: "property_contact_first_name",
      key: "property_contact_first_name",
      width: "200px",
      sorter: tableStringSorter("property_contact_first_name"),
      ...searchHilighter("property_contact_first_name"),
    },
    {
      title: "Property Contact Last Name",
      dataIndex: "property_contact_last_name",
      key: "property_contact_last_name",
      width: "200px",
      sorter: tableStringSorter("property_contact_last_name"),
      ...searchHilighter("property_contact_last_name"),
    },
    {
      title: "Property Contact Email",
      dataIndex: "property_contact_email",
      key: "property_contact_email",
      width: "200px",
      sorter: tableStringSorter("property_contact_email"),
      ...searchHilighter("property_contact_email"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "150px",
      sorter: tableStringSorter("status"),
      ...searchHilighter("status"),
      render: (idx, record) => textColor(record?.status),
      fixed: "right",
    },
  ];

  // columns that could be searched
  const searchableColumns = columns.map((col) => col.dataIndex);

  // custom hook for external multi column table data searching
  const {
    filteredTableData,
    searchedColumns,
    searchedWords,
    updateSearchMeta,
    searchMeta,
  } = useTableSearch({
    searchableColumns,
    allData: apiPropertiesData,
  });

  const propertiesData = useMemo(() => {
    let properties;

    if (Object.keys(searchMeta).length > 0 && filteredTableData) {
      properties = [...filteredTableData];
    } else {
      properties = customer !== "" ? apiPropertiesData : apiAllPropertiesData;
    }

    return properties?.map((property) => {
      const pms = pmsData?.find((pms) => pms?._id === property?.pms);
      return {
        property_name: property.property_name,
        property_code: property.property_code,
        brand: property.brand,
        pms: pms?.pms_name,
        pos: property.pos,
        property_time_zone: property.property_time_zone ?? "",
        status: property.status,
        property_contact_first_name: property?.property_contact_id?.first_name,
        property_contact_last_name: property?.property_contact_id?.last_name,
        property_contact_email: property?.property_contact_id?.email,
        customer_id: property.customer_id,
      };
    });
  }, [apiPropertiesData, apiAllPropertiesData, searchMeta, filteredTableData]);
  return (
    <CardElement title="System Discovery">
      <>
        <CustomerPropertiesTopBar
          updateSearchMeta={updateSearchMeta}
          customer={customer}
          setCustomer={setCustomer}
        />
        {!Array.isArray(propertiesData) || propertiesData.length === 0 ? (
          <Empty description="No Property found" />
        ) : (
          <div>
            <div className="informationViewer">
              <div className="information-container">
                <span className="information-descriptor">
                  Allowed Properties : &nbsp;
                </span>
                <span className="information-display">
                  {apiCustomerData.number_of_properties ?? 0}
                </span>
              </div>
              <div className="information-container">
                <span className="information-descriptor">
                  Approved Properties : &nbsp;
                </span>
                <span className="information-display">
                  {propertiesData?.filter(
                    (property) =>
                      property.status === ONBOARDING_PROPERTY_STATUS.APPROVED,
                  )?.length ?? 0}
                </span>
              </div>
              <div className="information-container">
                <span className="information-descriptor">
                  Uploaded Properties : &nbsp;
                </span>
                <span className="information-display">
                  {propertiesData?.length ?? 0}
                </span>
              </div>
              <div className="information-container">
                <span className="information-descriptor">
                  Pending Approval Properties : &nbsp;
                </span>
                <span className="information-display">
                  {propertiesData?.filter(
                    (property) =>
                      property.status ===
                      ONBOARDING_PROPERTY_STATUS.PENDING_APPROVAL,
                  )?.length ?? 0}
                </span>
              </div>
            </div>
            <HomePageTable>
              <Table
                className="onboarding-properties-table"
                columns={columns}
                dataSource={propertiesData}
                rowKey="_id"
                scroll={{ x: 1100, y: tableHeight }}
                pagination={{
                  defaultPageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "25", "50", "100"],
                }}
                locale={{
                  emptyText: isLoadingProperties ? (
                    <div className="spinner-table">
                      <LoadingComponent />
                    </div>
                  ) : null,
                }}
              />
            </HomePageTable>
          </div>
        )}
      </>
    </CardElement>
  );
};

export default OnboardingProperties;
